import {
  AUTH_STORAGE_MAX_AGE_MS,
  isTimestampExpired,
} from './registration.draft.model';

const CHALLENGE_REQUEST_SESSION_STORAGE_KEY =
  'new-registration-challenge-request-session-v1';

export const CODE_CHALLENGE_THROTTLE_MS = 120 * 1000;

type StoredChallengeRequestSession = {
  requestedAtByEmail: Record<string, number>;
};

const isWindowAvailable = (): boolean => {
  return typeof window !== 'undefined';
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return !!value && typeof value === 'object';
};

const normalizeEmailKey = (email: string): string => {
  return email.trim().toLowerCase();
};

const emptySession = (): StoredChallengeRequestSession => {
  return {
    requestedAtByEmail: {},
  };
};

const readSession = (): StoredChallengeRequestSession => {
  if (!isWindowAvailable()) {
    return emptySession();
  }

  try {
    const serialized = localStorage.getItem(
      CHALLENGE_REQUEST_SESSION_STORAGE_KEY,
    );
    if (!serialized) {
      return emptySession();
    }

    const parsed = JSON.parse(serialized) as unknown;
    if (!isRecord(parsed) || !isRecord(parsed.requestedAtByEmail)) {
      return emptySession();
    }

    const cleanedRequestedAtByEmail: Record<string, number> = {};

    Object.entries(parsed.requestedAtByEmail).forEach(
      ([email, requestedAt]) => {
        if (typeof requestedAt !== 'number') {
          return;
        }

        if (isTimestampExpired(requestedAt, AUTH_STORAGE_MAX_AGE_MS)) {
          return;
        }

        cleanedRequestedAtByEmail[normalizeEmailKey(email)] = requestedAt;
      },
    );

    return {
      requestedAtByEmail: cleanedRequestedAtByEmail,
    };
  } catch {
    return emptySession();
  }
};

const writeSession = (session: StoredChallengeRequestSession): void => {
  if (!isWindowAvailable()) {
    return;
  }

  try {
    localStorage.setItem(
      CHALLENGE_REQUEST_SESSION_STORAGE_KEY,
      JSON.stringify(session),
    );
  } catch {
    return;
  }
};

export const shouldSendEmailChallenge = (email: string): boolean => {
  const normalizedEmail = normalizeEmailKey(email);
  if (normalizedEmail.length === 0) {
    return true;
  }

  const session = readSession();
  const requestedAt = session.requestedAtByEmail[normalizedEmail];
  if (typeof requestedAt !== 'number') {
    return true;
  }

  return Date.now() - requestedAt >= CODE_CHALLENGE_THROTTLE_MS;
};

export const markEmailChallengeSent = (email: string): void => {
  const normalizedEmail = normalizeEmailKey(email);
  if (normalizedEmail.length === 0) {
    return;
  }

  const session = readSession();
  session.requestedAtByEmail[normalizedEmail] = Date.now();
  writeSession(session);
};

export const challengeRequestSessionStorageHelpers = {
  key: CHALLENGE_REQUEST_SESSION_STORAGE_KEY,
};
